import React, { useMemo } from 'react';

import { Button, Grid, Group, Modal, Stack, Text, Title } from '@mantine/core';
import { AxiosError, AxiosResponse } from 'axios';
import KiloAlert from 'components/KiloAlert';
import { UseMutationResult, useQueryClient, UseQueryResult } from 'react-query';
import {
  Athlete,
  AthletePackageRetrieve,
  AthletePackageUpdate,
  AthletePackageUpdateResponse,
  ErrorMessage,
} from 'types';
import notificationHandler from 'utils/notifications';

interface IAutoRenewModalProps {
  opened: boolean;
  athletePackage: AthletePackageRetrieve;
  closeHandler: () => void;
  isDesktop: boolean;
  updateAthletePackageMutation: UseMutationResult<
    AxiosResponse<AthletePackageUpdateResponse>,
    AxiosError<ErrorMessage>,
    AthletePackageUpdate
  >;
}

const AutoRenewModal = ({
  opened,
  athletePackage,
  closeHandler,
  updateAthletePackageMutation,
  isDesktop,
}: IAutoRenewModalProps): JSX.Element => {
  const { successNotification, errorNotification } = notificationHandler();
  const queryClient = useQueryClient();

  const athlete = queryClient.getQueryData<UseQueryResult<Athlete>>(['athletes', athletePackage.athlete]);
  const athleteName = useMemo(() => {
    if (!athlete || !athlete.data) return '';
    return athlete.data.fullName;
  }, [athlete]);

  const handleToggleAutoRenew = (): void => {
    const payload: AthletePackageUpdate = {
      action: 'auto_renew',
      athletePackageId: athletePackage.athletePackageId,
      autoRenew: !athletePackage.autoRenew,
      autoPay: athletePackage.autoPay,
      paymentMethod: athletePackage.paymentMethod,
      nonStripePaymentMethod: athletePackage.nonStripePaymentMethod,
      athleteStatusOnStart: athletePackage.athleteStatusOnStart,
      athleteStatusOnEnd: athletePackage.athleteStatusOnEnd,
    };
    updateAthletePackageMutation.mutate(payload, {
      onError: error => {
        errorNotification('Athlete package update failed', error.response?.data.message);
      },
      onSuccess: () => {
        successNotification('Athlete package update success', `Athlete package successfully updated.`);
        closeHandler();
      },
    });
  };

  return (
    <Modal
      opened={opened}
      onClose={closeHandler}
      size='lg'
      centered
      title={
        <Title order={4} pr='md'>
          Disable Auto-Renew
        </Title>
      }
    >
      <Stack p='md' mt={isDesktop ? 20 : 0} spacing={'xl'}>
        <Grid mb={48} style={{ rowGap: 24, columnGap: 55, width: 'fit-content', margin: '0 auto' }}>
          <Grid.Col span={4}>
            <Text color='#7A838B' fw={500} fz={16} italic>
              Athlete Name
            </Text>
            <Text color='#7A838B' fw={500} fz={16} italic>
              Package
            </Text>
          </Grid.Col>
          <Grid.Col span={4}>
            <Text fw={400} fz={16} style={{ whiteSpace: 'nowrap' }}>
              {athleteName}
            </Text>
            <Text fw={400} fz={16} style={{ whiteSpace: 'nowrap' }}>
              {athletePackage.package.name}
            </Text>
          </Grid.Col>
        </Grid>
        {athletePackage.child && (
          <KiloAlert w={isDesktop ? '90%' : '100%'} variant='filled' variantStyle='warning'>
            <Text fz={14}>
              By disabling Auto-Renew an upcoming package will be removed. Invoices with billing activity
              will be preserved.{' '}
            </Text>
          </KiloAlert>
        )}

        <Text fz={16} italic m={'auto'}>
          Are you sure you want to disable Auto-Renew for this package?
        </Text>
      </Stack>
      <Group position='right' pt='md'>
        <Button size='xs' radius='md' type='submit' onClick={handleToggleAutoRenew}>
          Disable Auto-Renew
        </Button>
      </Group>
    </Modal>
  );
};

export default AutoRenewModal;
